import { useState, useEffect, useCallback } from 'react';
import { preferencesAPI } from '@/lib/supabase/preferences';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- recall pages read free-form JSON
type DealerPreferenceMap = Record<string, any>;

/**
 * Read-only tenant dealer preferences (excluded wins across users), used by the
 * recall pages to grey out excluded dealers. Writes go through set_dealer_config
 * (src/lib/supabase/dealerConfig.ts) from the Dealers screen.
 */
// eslint-disable-next-line max-lines-per-function
export const useDealerPreferences = (userId?: string) => {
  const [dealerPreferences, setDealerPreferences] = useState<DealerPreferenceMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(true);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load preferences from localStorage as fallback
  const loadFromLocalStorage = useCallback((): DealerPreferenceMap => {
    try {
      const savedPreferences = localStorage.getItem('dealerPreferences');
      if (savedPreferences) {
        return JSON.parse(savedPreferences);
      }
    } catch {
      // Fall through to empty preferences when localStorage payload is invalid.
    }
    return {};
  }, []);

  // Save to localStorage as backup
  const saveToLocalStorage = useCallback((preferences: DealerPreferenceMap) => {
    try {
      localStorage.setItem('dealerPreferences', JSON.stringify(preferences));
    } catch {
      // Best effort only.
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (userId && isOnline) {
          const preferences = await preferencesAPI.getDealerPreferences(userId);
          setDealerPreferences(preferences);
          saveToLocalStorage(preferences);
        } else {
          setDealerPreferences(loadFromLocalStorage());
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dealer data');
        setDealerPreferences(loadFromLocalStorage());
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId, isOnline, loadFromLocalStorage, saveToLocalStorage]);

  const getDealerPreference = useCallback(
    (dealerName: string) => {
      if (!dealerName) return { is_included: true };

      // Normalize dealer name for matching (trim and case-insensitive)
      const normalizedName = dealerName.trim();

      // Try exact match first
      if (dealerPreferences[normalizedName]) {
        return dealerPreferences[normalizedName];
      }

      // Try case-insensitive match
      const lowerName = normalizedName.toLowerCase();
      const matchingKey = Object.keys(dealerPreferences).find(
        key => key.trim().toLowerCase() === lowerName
      );

      if (matchingKey) {
        return dealerPreferences[matchingKey];
      }

      // Default to included if no preference found
      return { is_included: true };
    },
    [dealerPreferences]
  );

  return {
    dealerPreferences,
    getDealerPreference,
    loading,
    error,
    isOnline,
    isAuthenticated: !!userId
  };
};
